import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Building2, TrendingUp, ScrollText, CheckCircle2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
      }
    }
  }, [location]);

  // Services with direct booking
  const incomeTaxServicesWithBooking = [
    { name: 'ITR Filing - Salary Individual Basic (Income below 12 Lacs)', link: '/services/itr-filing-basic', price: '₹1,350' },
    { name: 'ITR Filing - Salary Individual Standard (12 Lacs & Above)', link: '/services/itr-filing-standard', price: '₹1,450' },
    { name: 'ITR Filing - Capital Gains', link: '/services/itr-capital-gains', price: '₹2,400' },
    { name: 'ITR Filing - F&O and Trading', link: '/services/itr-fno-trading', price: '₹4,500' },
    { name: 'ITR Filing - Foreign Income & Assets', link: '/services/itr-foreign-income', price: '₹5,600' },
    { name: 'New PAN Card Application', link: '/services/pan-card-new', price: '₹300' },
    { name: 'PAN Card Changes / Corrections', link: '/services/pan-card-change', price: '₹400' },
    { name: 'Minor PAN Card', link: '/services/minor-pan-card', price: '₹500' },
    { name: 'Gumasta License', link: '/services/gumasta-license', price: '₹1,200' },
  ];

  const incomeTaxServicesContactUs = [
    'Responding to Income Tax Notices',
    'PAN Card Surrender',
    'Incorporation of Partnership Firm',
    'LLP Incorporation',
    'Private Limited Company Incorporation',
    'Income Tax Scrutiny Handling',
    'Income Tax Appeal',
    'HUF Incorporation',
    'HUF PAN Card'
  ];

  const gstServicesWithBooking = [
    { name: 'GST Registration', link: '/services/gst-registration', price: '₹2,500' },
    { name: 'GST NIL Return Filing', link: '/services/gst-nil-return', price: '₹400' },
    { name: 'GST Filing (Less than 5 Invoices)', link: '/services/gst-filing-small', price: '₹500' },
    { name: 'GST Return Filing', link: '/services/gst-return-filing', price: 'From ₹500' },
    { name: 'GST Amendment', link: '/services/gst-amendment', price: '₹800' },
  ];

  const financialPlanningServices = [
    'Financial Blueprint Preparation',
    'Term Insurance',
    'Health Insurance',
    'Personal Accident Insurance Policy',
    'Opening of E-Wealth Account',
    'One on One Financial Consultation'
  ];

  const estatePlanningServices = [
    'Will Drafting',
    'Registered Will',
    'Trust Formation (Coming Soon)'
  ];

  return (
    <div className="bg-background py-24 md:py-32" data-testid="services-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-sm font-medium tracking-wide uppercase text-secondary mb-4">Our Services</p>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight font-playfair mb-6" data-testid="services-title">
            Tax, GST & Estate, Financial Planning Services in Indore
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl mx-auto">
            Professional Tax & Financial Advisory Services in Indore. Trust our 38+ years of expertise for Income Tax, GST, and Comprehensive Financial Planning.
          </p>
        </div>

        {/* Income Tax Services */}
        <section className="mb-16" data-testid="income-tax-section">
          <Card className="overflow-hidden">
            <CardHeader className="bg-primary text-primary-foreground p-8">
              <div className="flex items-center gap-4">
                <div className="bg-secondary p-3 rounded-lg">
                  <FileText className="h-8 w-8" />
                </div>
                <div>
                  <CardTitle className="text-3xl font-playfair">Income Tax Services</CardTitle>
                  <p className="text-sm opacity-90 mt-1">Complete tax filing and compliance solutions</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              {/* Services with Direct Booking */}
              <div className="mb-8">
                <h4 className="font-semibold mb-4 text-lg">Apply Now for These Services:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {incomeTaxServicesWithBooking.map((service, index) => (
                    <Link key={index} to={service.link}>
                      <Button variant="outline" className="w-full justify-between h-auto py-3 text-left" data-testid={`book-service-${index}`}>
                        <span className="text-sm">{service.name}</span>
                        <span className="font-bold text-secondary ml-2">{service.price}</span>
                      </Button>
                    </Link>
                  ))}
                </div>
              </div>
              
              {/* Other Services - Contact Required */}
              <div className="border-t pt-6">
                <h4 className="font-semibold mb-4 text-lg">Other Services (Contact for Pricing):</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {incomeTaxServicesContactUs.map((service, index) => (
                    <div key={index} className="flex items-start gap-2" data-testid={`income-tax-service-contact-${index}`}>
                      <CheckCircle2 className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{service}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <Link to="/contact">
                    <Button variant="default" className="bg-primary" data-testid="contact-for-services">
                      Contact Us for These Services
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* GST Services */}
        <section className="mb-16" data-testid="gst-section">
          <Card className="overflow-hidden">
            <CardHeader className="bg-secondary text-secondary-foreground p-8">
              <div className="flex items-center gap-4">
                <div className="bg-primary p-3 rounded-lg">
                  <Building2 className="h-8 w-8 text-white" />
                </div>
                <div>
                  <CardTitle className="text-3xl font-playfair">GST Services</CardTitle>
                  <p className="text-sm opacity-90 mt-1">GST registration, filing, and compliance</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              {/* GST Services with Direct Booking */}
              <div className="mb-8">
                <h4 className="font-semibold mb-4 text-lg">Apply Now for GST Services:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {gstServicesWithBooking.map((service, index) => (
                    <Link key={index} to={service.link}>
                      <Button variant="outline" className="w-full justify-between h-auto py-3 text-left" data-testid={`book-gst-${index}`}>
                        <span className="text-sm">{service.name}</span>
                        <span className="font-bold text-secondary ml-2">{service.price}</span>
                      </Button>
                    </Link>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Personal Financial Planning */}
        <section className="mb-16" data-testid="financial-planning-section">
          <Card className="overflow-hidden">
            <CardHeader className="bg-accent text-accent-foreground p-8">
              <div className="flex items-center gap-4">
                <div className="bg-white p-3 rounded-lg">
                  <TrendingUp className="h-8 w-8 text-accent" />
                </div>
                <div>
                  <CardTitle className="text-3xl font-playfair">Personal Financial Planning</CardTitle>
                  <p className="text-sm opacity-90 mt-1">Comprehensive wealth management and insurance</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {financialPlanningServices.map((service, index) => (
                  <div key={index} className="flex items-start gap-2" data-testid={`financial-planning-service-${index}`}>
                    <CheckCircle2 className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{service}</span>
                  </div>
                ))}
              </div>
              
              {/* Financial Consultation Booking */}
              <div className="border-t pt-6">
                <h4 className="font-semibold mb-4">Book Your Consultation:</h4>
                <a href="https://topmate.io/jainesh_kothari/62027" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-secondary hover:bg-secondary/90" data-testid="book-financial-consultation">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Book One on One Financial Consultation
                  </Button>
                </a>
                <p className="text-sm text-muted-foreground mt-3">
                  Get personalized financial planning advice from our Certified Financial Planner®
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Estate Planning */}
        <section className="mb-16" id="estate-planning" data-testid="estate-planning-section">
          <Card className="overflow-hidden">
            <CardHeader className="bg-primary text-primary-foreground p-8">
              <div className="flex items-center gap-4">
                <div className="bg-secondary p-3 rounded-lg">
                  <ScrollText className="h-8 w-8" />
                </div>
                <div>
                  <CardTitle className="text-3xl font-playfair">Estate Planning</CardTitle>
                  <p className="text-sm opacity-90 mt-1">Secure your legacy for future generations</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {estatePlanningServices.map((service, index) => (
                  <div key={index} className="flex items-start gap-2" data-testid={`estate-planning-service-${index}`}>
                    <CheckCircle2 className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">
                      {service.includes('Coming Soon') ? (
                        <>
                          Trust Formation{' '}
                          <span className="inline-block ml-1 px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800 rounded-full">Coming Soon</span>
                        </>
                      ) : service}
                    </span>
                  </div>
                ))}
              </div>

              {/* Book Appointment */}
              <div className="border-t pt-6">
                <h4 className="font-semibold mb-4">Book Your Appointment:</h4>
                <a href="https://topmate.io/jainesh_kothari/2050039" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-secondary hover:bg-secondary/90" data-testid="book-estate-planning-appointment">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Book Appointment for Estate Planning
                  </Button>
                </a>
                <p className="text-sm text-muted-foreground mt-3">
                  Schedule a consultation to discuss Will Drafting, Registered Will, or Trust Formation with our experts.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <div className="text-center bg-muted/20 p-12 rounded-xl">
          <h3 className="text-2xl font-semibold font-playfair mb-4" data-testid="services-cta-title">
            Ready to Get Started?
          </h3>
          <p className="text-muted-foreground mb-6">
            Contact us today to discuss how we can help with your tax and financial needs.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-secondary hover:bg-secondary/90" data-testid="services-contact-button">
              Contact Us Now
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Services;
